import React, { useEffect, useState } from 'react';
import { ActivityIndicator, FlatList, Image, KeyboardAvoidingView, Modal, Platform } from 'react-native';
import type { BEEventProps, BracketCompetitionProps, BracketProps, CompanyProps, CompetitionPayoutTypeProps, CompetitionProps, CompetitionResultTypeProps, CompetitionSeasonProps, CompetitionTypeProps, EventProps, FocusPositionProps, LeagueProps, PollCampaignProps, PublicPlayerProps, SquaresCompetitionProps } from '../types';
import { EngageApi, EngageHelpers } from './api';
import CompetitionCard from './components/CompetitionCard';
import { Icons, Toggle } from '../Components';
import SquaresCompetitionCard from './components/SquaresCompetitionCard';
import BracketCompetitionCard from './components/BracketCompetitionCard';
import moment from 'moment-mini';
import SocketManager from '../Socket';
import SeasonCard from './components/SeasonCard';
import EngageHeader from './components/EngageHeader';
import PrivateCodePrompt from './components/PrivateCodePrompt';
import { Button, Text, View } from '../Components/Themed';
import { useColors } from '../constants/useColors';
import PollCampaignCard from './components/PollCampaignCard';


type EngageModuleProps = {
    player_id?:string,
    insets?: { top:number, bottom:number },
    view_mode?:'desktop'|'mobile'
    init_engagement?:'competitions'|'brackets'|'squares'|'polls',
    onEvent?:(be_event:BEEventProps) => void,
    onFocusPosition?:(pos:FocusPositionProps) => void,
    onManage?:() => void,
    onBracketRoom?:() => void,
    onCreateSeason?:() => void,
    onSelectCompany?:(c:CompanyProps) => void,
    onCreateCompetition?:() => void,
    onCreateSquares?: () => void,
    onCreateBracketCompetition?:() => void,
    onSelectCompetition:(c:CompetitionProps) => void,
    onSelectBracketCompetition:(bc:BracketCompetitionProps) => void,
    onSelectPollCampaign:(pc:PollCampaignProps) => void,
    onSelectSquaresCompetition:(sc:SquaresCompetitionProps) => void,
    onSelectCompetitionSeason:(cs:CompetitionSeasonProps) => void
}

const engage_sections = ['header','toggle', 'private','engagements']
const MIN_ENGAGEMENT_WIDTH = 325
const EngageModule = ({ view_mode, insets, init_engagement, onSelectPollCampaign, onBracketRoom, onSelectBracketCompetition, onSelectCompetition, onSelectSquaresCompetition, onSelectCompetitionSeason,
    onCreateSeason, onCreateCompetition, onCreateBracketCompetition, onCreateSquares, onFocusPosition, onManage, onEvent, onSelectCompany
 }:EngageModuleProps) => {
    const [ module_size, setModuleSize ] = useState({ width:0, height:0 });
    const [ show_code_prompt, setShowCodePrompt ] = useState(false);
    const [ socket_state, setSocketState ] = useState<{
        connected:boolean,
        reload_needed?:boolean
    }>({
        connected: false
    });
    const [ active_tab, setActiveTab ] = useState(init_engagement ?? 'competitions');
    const [ module_data, setModuleData ] = useState<{
        loading:boolean,
        competitions:CompetitionProps[],
        poll_campaigns:PollCampaignProps[],
        competition_seasons:CompetitionSeasonProps[],
        competition_types:CompetitionTypeProps[],
        competition_result_types:CompetitionResultTypeProps[],
        competition_payouts:CompetitionPayoutTypeProps[],
        selected_bracket?:BracketProps,
        events:EventProps[],
        leagues:LeagueProps[],
        brackets:BracketProps[],
        squares_competitions:SquaresCompetitionProps[],
        bracket_competitions:BracketCompetitionProps[],
        companies:CompanyProps[],
        players:PublicPlayerProps[],
    }>({
        loading:false,
        competitions: [],
        competition_seasons:[],
        poll_campaigns: [],
        companies:[],
        leagues:[],
        events:[],
        brackets:[],
        competition_types: [],
        competition_result_types: [],
        competition_payouts: [],
        squares_competitions: [],
        bracket_competitions:[],
        players:[]
    });
    const { loading, competitions, poll_campaigns, selected_bracket, competition_seasons, competition_result_types, squares_competitions, brackets, leagues, bracket_competitions, events, competition_types, companies, players } = module_data;
    const Colors = useColors();

    const visible_competitions = competitions.filter(c => moment().isBefore(moment(c.scheduled_datetime)))

    const available_brackets = brackets.filter(b => moment().isBefore(moment(b.scheduled_datetime)) && bracket_competitions.map(bc => bc.bracket_id).includes(b.bracket_id))
    let visible_bracket_competitions = [ ...bracket_competitions.filter(b => moment().isBefore(moment(b.scheduled_datetime))) ]
    if(selected_bracket){
        visible_bracket_competitions = visible_bracket_competitions.filter(bc => bc.bracket_id == selected_bracket.bracket_id);
    }
    let engagement_width = view_mode == 'mobile' ? module_size.width - 20 : (module_size.width / 3) - 20
    if(engagement_width < MIN_ENGAGEMENT_WIDTH){ engagement_width = MIN_ENGAGEMENT_WIDTH }
    useEffect(() => {
        EngageApi.setEnvironment();
        getDataFromServer()
    },[])

    useEffect(() => {
        if(!socket_state.connected){ return }
        if(socket_state.connected && !socket_state.reload_needed){ return }
        getDataFromServer()
    },[socket_state.connected])

    const getDataFromServer = async() => {
        setModuleData({ 
            ...module_data,
            loading:true,
            
        })
        const lgs = await EngageApi.getLeagues();
        const comps = await EngageApi.getActivePublicCompetitions();
        const seasons = await EngageApi.getActivePublicSeasons();
        const brackets = await EngageApi.getActivePublicBrackets();
        const pcs = await EngageApi.getActivePublicCampaigns();
        const squares = await EngageApi.getActivePublicSquares();
        const company_ids = EngageHelpers.getCompanyIdsFromEngage(comps, squares, brackets, pcs);
        const cpanys = await EngageApi.getCompaniesByIds(company_ids);
        const player_ids = EngageHelpers.getPlayerIdsFromEngage(comps, squares, brackets, pcs);
        const plyers = await EngageApi.getPlayersByPlayerIds(player_ids)
        const opts = await EngageApi.getCompetitionOptions();
        const event_ids = EngageHelpers.getEventIdsFromSquares(squares);
        const evts = await EngageApi.getEventsByEventIds(event_ids);
        const bracket_ids = EngageHelpers.getBracketIdsFromBracketComps(brackets);
        const bcks = await EngageApi.getBracketsByIds(bracket_ids)

        setModuleData({ 
            ...module_data,
            loading:false,
            competitions:comps,
            competition_seasons: seasons,
            poll_campaigns: pcs.filter(pc => (pc.active_polls??0) > 0),
            bracket_competitions:brackets,
            competition_types: opts.competition_types,
            competition_result_types: opts.competition_result_types,
            competition_payouts: opts.competition_payout_types,
            squares_competitions: squares,
            companies: cpanys,
            events: evts,
            brackets: bcks,
            leagues: lgs,
            players: plyers
        })
    }


    const renderSquaresCompetitions = (data: { item:SquaresCompetitionProps, index:number }) => {
        const admin = players.find(p => p.player_id == data.item.admin_id);
        const company = companies.find(c => c.company_id == data.item.company_id);
        const event = events.find(e => e.event_id == data.item.event_id)
        return (
            <View key={data.item.sq_comp_id.toString()} transparent style={{ margin:10, width: engagement_width }}>
                <SquaresCompetitionCard
                    squares_competition={data.item}
                    company={company}
                    admin={admin}
                    event={event}
                    onSelectCompetition={(sc) => onSelectSquaresCompetition(sc)}
                />
            </View>
        )
    }


    const renderBracketCompetitions = (data: { item:BracketCompetitionProps, index:number }) => {
        const admin = players.find(p => p.player_id == data.item.admin_id);
        const company = companies.find(c => c.company_id == data.item.company_id);
        const competition_result_type = competition_result_types.find(t => t.competition_result_type_id == data.item.competition_result_type_id);
        const bracket = brackets.find(b => b.bracket_id == data.item.bracket_id);
        const league = leagues.find(e => e.league_id == bracket?.league_id)
        if(!competition_result_type){ return <></> }
        return (
            <View key={data.item.bracket_competition_id.toString()} transparent style={{ margin:10, width: engagement_width }}>
                <BracketCompetitionCard
                    bracket_competition={data.item}
                    company={company}
                    competition_result_type={competition_result_type}
                    admin={admin}
                    bracket={bracket}
                    
                    league={league}
                    onCompetitionSelect={(sc) => onSelectBracketCompetition(sc)}
                />
            </View>
        )
    }
    const renderSeasons = (data: { item:CompetitionSeasonProps, index:number }) => {
        return (
            <View>
                <SeasonCard
                    competition_season={data.item}
                    width={module_size.width / 2.5}
                    onSelectSeason={(cs) => onSelectCompetitionSeason(cs)}
                />
            </View>
        )
    }

    const renderBrackets = (data: { item:BracketProps, index:number }) => {
        const selected = selected_bracket?.bracket_id == data.item.bracket_id ? true : false
        const league = leagues.find(l => l.league_id == data.item.league_id);
        return (
            <Button
                float
                type={selected ? 'action' : 'text'}
                style={{ padding:10, flexDirection:'row', alignItems:'center', margin:4, borderRadius:100 }}
                onPress={() => selected ? setModuleData({ ...module_data, selected_bracket: undefined }): setModuleData({ ...module_data, selected_bracket:data.item })}
            >
                {league ?
                <Image
                    source={{ uri: league.league_image }}
                    style={{ height:25, width:25, marginRight:10 }}
                    resizeMode='cover'
                />
                :<></>}
                <Text style={{ maxWidth:150 }} textAlign='center' theme='h2' color={selected?Colors.text.white : Colors.text.h1}>{data.item.bracket_name}</Text>
            </Button>
        )
    }

    const renderCompetitions = (data: { item:CompetitionProps, index:number }) => {
        const admin = players.find(p => p.player_id == data.item.admin_id);
        const pacer = players.find(p => p.player_id == data.item.pacer_id);
        const company = companies.find(c => c.company_id == data.item.company_id)
        const competition_type = competition_types.find(t => t.competition_type_id == data.item.competition_type_id);
        const competition_result_type = competition_result_types.find(t => t.competition_result_type_id == data.item.competition_result_type_id);
        if(!competition_type || !competition_result_type){ return <></> }
        return (
            <View key={data.item.competition_id.toString()} transparent style={{ margin:10, width:engagement_width }}>
                <CompetitionCard 
                    competition={data.item}
                    company={company}
                    competition_type={competition_type}
                    competition_result_type={competition_result_type}
                    admin={admin}
                    pacer={pacer}
                    onCompetitionSelect={(c) => onSelectCompetition(c)}
                />
            </View>
        )
    }

    const renderPollCampaigns = (data: { item:PollCampaignProps, index:number }) => {
        const company = companies.find(c => c.company_id == data.item.company_id)
        const admin = players.find(p => p.player_id == data.item.player_id);
        return (
            <View key={data.item.poll_campaign_id.toString()} transparent style={{ margin:10, width:engagement_width }}>
                <PollCampaignCard 
                    poll_campaign={data.item}
                    company={company}
                    admin={admin}
                    onSelectPollCampaign={(c) => onSelectPollCampaign(c)}
                />
            </View>
        )
    }

    const renderSections = (data:{item:string, index:number}) => {
        switch(data.item){
            case 'header':
                return (
                    <EngageHeader
                    companies={companies}
                    width={module_size.width}
                    view_mode={view_mode}
                    onManage={onManage}
                    onEvent={onEvent}
                    onSelectCompany={onSelectCompany}
                />
                )
            case 'toggle':
                let toggles = [
                    {key:'competitions', label: 'Competitions'},
                    {key:'brackets', label: 'Brackets'},
                ]
                if(poll_campaigns.length > 0){ toggles.push({ key: 'polls', label: 'Predict / Trivia' }) }
                if(squares_competitions.length > 0){ toggles.push({ key: 'squares', label: 'Squares' }) }
                return (
                    <View transparent style={{ margin:10 }}>
                        {onManage && view_mode == 'mobile' ?
                            <Button
                            title='Manage competitions you have created'
                            style={{ paddingRight:20, alignSelf:'flex-end' }}
                            type='text'
                            onPress={() => onManage()}
                        />
                        :<></>}
                        <Toggle
                            background_color={Colors.views.header}
                            inactive_title_color={Colors.text.h1}
                            options={toggles}
                            selected_option={active_tab}
                            onSelectOption={(option) => setActiveTab(option as any)}
                        />
                    </View>
                )
            case 'private':
                return (
                    <View type='header' style={{ padding:10, marginBottom:10, flexDirection:'row', alignItems:'center' }}>
                        <Icons.EyeOffIcon size={18} color={Colors.text.h1} />
                        <View transparent style={{ flex:1, marginLeft:10 }}>
                            <Text theme='h1'>Find Private</Text>
                            <Text style={{ marginTop:3 }} theme='description'>Did you receive a private code? Enter it here.</Text>
                        </View>
                        <Button
                            title='ENTER PRIVATE CODE'
                            type='text'
                            onPress={() => setShowCodePrompt(true)}
                        />
                    </View>
                )
            case 'engagements':
                return (
                    <View transparent>
                        {active_tab == 'competitions' ?
                        <View transparent>
                            {competition_seasons.length > 0 ?
                            <View transparent style={{ marginTop:10 }}>
                                <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                    <View style={{ flex:1 }}>
                                        <Text theme='h1'>Public Seasons</Text>
                                        <Text style={{ marginTop:3 }} theme='description'>Seasons are multi-competition contests. Payouts are split between each competition and the season long winners.</Text>
                                    </View>
                                    {onCreateSeason ?
                                    <Button
                                        title='NEW'
                                        type='success'
                                        onPress={() => onCreateSeason()}
                                    />
                                    :<></>}
                                </View>
                                <FlatList
                                    data={competition_seasons.sort((a,b) => moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix())}
                                    horizontal
                                    keyExtractor={(item) => item.competition_season_id.toString()}
                                    renderItem={renderSeasons}
                                />
                            </View>
                            :<></>}
                            <View transparent style={{ marginBottom:15, marginTop:10 }}>
                                <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                    <View transparent style={{ flex:1 }}>
                                        <Text theme='h1'>Public Competitions</Text>
                                        <Text style={{ marginTop:3 }} theme='description'>Below are available pick-em and wager based competitions.  All competitions are offered with 0 fees. All ticket sales are paid out to the winners!</Text>
                                    </View>
                                    {onCreateCompetition ?
                                    <Button
                                        title='NEW'
                                        type='success'
                                        onPress={() => onCreateCompetition()}
                                    />
                                    :<></>}
                                    
                                </View>
                                <View style={{ flexDirection:'row', flexWrap:'wrap', justifyContent:'center' }}>
                                    {visible_competitions.sort((a,b) => moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix()).map((c, i) => {
                                        return renderCompetitions({ item: c, index: i })
                                    })}
                                </View>
                                
                            </View>
                        </View>
                        :active_tab == 'squares' ?
                        <View transparent>
                            <View transparent style={{ marginBottom:15, marginTop:10 }}>
                                <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                    <View transparent style={{ flex:1 }}>
                                        <Text theme='h1'>Public Auction Squares</Text>
                                        <Text style={{ marginTop:3 }} theme='description'>Below are available squares competitions.  Bid on squares until the auction period ends.  Win $ and prizes when your square hits at the end of each quarter!</Text>
                                    </View>
                                    {onCreateSquares ?
                                    <Button
                                        title='NEW'
                                        type='success'
                                        onPress={() => onCreateSquares()}
                                    />
                                    :<></>}
                                </View>
                            </View>
                            <View style={{ flexDirection:'row', flexWrap:'wrap', justifyContent:'center' }}>

                            {squares_competitions.sort((a,b) => moment(a.begin_datetime).unix() - moment(b.begin_datetime).unix()).map((c, i) => {
                                return renderSquaresCompetitions({ item: c, index: i })
                            })}
                            </View>
                        </View>
                        :active_tab == 'polls' ?
                        <View transparent>
                            <View transparent style={{ marginBottom:15, marginTop:10 }}>
                                <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                    <View transparent style={{ flex:1 }}>
                                        <Text theme='h1'>Prediction / Trivia Games</Text>
                                        <Text style={{ marginTop:3 }} theme='description'>Below are available prediction or trivia games.</Text>
                                    </View>
                                </View>
                            </View>
                            <View style={{ flexDirection:'row', flexWrap:'wrap', justifyContent:'center' }}>
                                {poll_campaigns.sort((a,b) => moment(b.create_datetime).unix() - moment(a.create_datetime).unix()).map((c, i) => {
                                    return renderPollCampaigns({ item: c, index: i })
                                })}
                            </View>
                        </View>
                        :active_tab == 'brackets' ?
                        <View transparent>
                            <View transparent>
                                {view_mode == 'mobile' && onBracketRoom ?
                                <View float style={{ flexDirection:'row', alignItems:'center', margin:4, padding:10, marginBottom:10 }}>
                                    <View style={{ flex:1, marginRight:10 }}>
                                        <Text theme='h1'>Bracket Home</Text>
                                        <Text theme='description'>Go to bracket room to manage all your brackets and find new ones to complete.</Text>
                                    </View>
                                    <Button
                                        type='action'
                                        title='Bracket Room'
                                        onPress={() => onBracketRoom()}
                                    />
                                </View>

                                :<></>}
                                <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                    <View transparent style={{ flex:1 }}>
                                        <Text theme='h1'>Public Bracket Competitions</Text>
                                        <Text style={{ marginTop:3 }} theme='description'>Below are available bracket competitions.  All competitions are offered with 0 fees. All ticket sales are paid out to the winners!</Text>
                                    </View>
                                    {onCreateBracketCompetition ?
                                    <Button
                                        title='NEW'
                                        type='success'
                                        onPress={() => onCreateBracketCompetition()}
                                    />
                                    :<></>}
                                    {view_mode == 'desktop' && onBracketRoom ?
                                    <Button
                                        type='action'
                                        title='Bracket Room'
                                        onPress={() => onBracketRoom()}
                                    />
                                    :<></>}
                                </View>
                            </View>
                            <View style={{ marginTop:10 }}>
                                <FlatList
                                    data={available_brackets}
                                    key={'bracket_list'}
                                    horizontal
                                    renderItem={renderBrackets}
                                    keyExtractor={(item) => item.bracket_id.toString()}
                                />
                                <View style={{ flexDirection:'row', flexWrap:'wrap', justifyContent:'center' }}>
                                    {visible_bracket_competitions.sort((a,b) => moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix()).map((c, i) => {
                                        return renderBracketCompetitions({ item: c, index: i })
                                    })}
                                </View>
                            </View>
                        </View>
                        :<></>}
                    </View>
                )
            default: return <></>
        }
    }

    return (
        <View style={{ flexGrow:1 }} onLayout={(ev) => {
            const { height, width } = ev.nativeEvent.layout;
            setModuleSize({ width, height })
        }}>
            <FlatList
                data={engage_sections}
                key={`engage_sections`}
                keyExtractor={(item) => item}
                renderItem={renderSections}
                refreshing={loading}
                onRefresh={() => getDataFromServer()}
            />
            
            {show_code_prompt ?
            <Modal
                transparent
                animationType={view_mode == 'desktop' ? 'fade': 'slide'}
                style={{ flex:1 }}
            >
                <KeyboardAvoidingView
                    style={{ flex:1 }}
                    behavior={Platform.OS == 'ios' ? 'padding' : 'height'}
                >
                <View type='blur' style={view_mode == 'desktop'?{ flex:1, justifyContent:'center', alignItems:'center' }:{ flex:1, justifyContent:'flex-end', paddingBottom:insets?.bottom }}>
                    <View transparent style={view_mode=='desktop'?{ width:500 }: {}}>
                    <PrivateCodePrompt
                        onFocusPosition={onFocusPosition}
                        onFoundBracket={(b) => {
                            onSelectBracketCompetition(b)
                            setShowCodePrompt(false);
                        }}
                        onFoundCompetition={(c) => {
                            onSelectCompetition(c)
                            setShowCodePrompt(false);

                        }}
                        onFoundSeason={(s) => {
                            onSelectCompetitionSeason(s)
                            setShowCodePrompt(false);
                        }} 
                        onFoundSquaresCompetition={(sc) => {
                            onSelectSquaresCompetition(sc)
                            setShowCodePrompt(false);
                        }}
                        onClose={() => setShowCodePrompt(false)}
                    />
                    </View>
                </View>
                </KeyboardAvoidingView>
            </Modal>
            :<></>}
            {loading ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, alignItems:'center', padding:20 }}>
                <ActivityIndicator
                    size='large'
                    color={Colors.text.h1}
                />
            </View>
            :<></>}
            <SocketManager
                onConnect={() => setSocketState({ ...socket_state, connected: true })}
                subscribed_events={[]}
                onSocketEvent={() => { return }}
                onDisconnect={() => setSocketState({ ...socket_state, connected: false, reload_needed: true })}
            />
        </View>
    )
}

export default EngageModule